import { logger } from "cayo.ui";
import { resolvePropertyValue } from "@cayo/shared/components/Form/modelUtils/auxPropsUtils";

class LogicalODataExpression {
  private logicalOperators = ["and", "or"];
  private object: any;
  private expression: string;
  private readonly log = logger.getLogger("LogicalODataExpression");
  constructor(expression: string) {
    if (!expression.startsWith("(")) {
      expression = "(" + expression + ")";
    }

    this.expression = expression;
  }

  private calcSimpleExpression = (prop: string, op: string, value: string | null | undefined) => {
    const fixedProp = prop.toLowerCase();
    const propName = Object.keys(this.object).find((k) => k.toLowerCase() === fixedProp);
    try {
      const propValue = propName ? this.object[propName!] : resolvePropertyValue(prop, this.object);
      switch (op) {
        case "ne":
          return value === null ? propValue !== value : propValue?.toString() !== value;
        case "eq":
          return value === null ? propValue === value : propValue?.toString() === value;
        case "gt":
          return value !== null && value !== undefined ? propValue > value : false;
        case "ge":
          return value !== null && value !== undefined ? propValue >= value : false;
        case "lt":
          return value !== null && value !== undefined ? propValue < value : false;
        case "le":
          return value !== null && value !== undefined ? propValue <= value : false;
        default:
          if (!op && typeof propValue === "boolean") {
            return propValue;
          }

          if (!op && !value && propValue === undefined) {
            return false;
          }
          this.log.error(`Unsupported operator: ${prop}, op: ${op}, value: ${value}`);

          return undefined;
        //throw new Error("Unsupported");
      }
    } catch (e) {
      this.log.error(`Failed to calc expression. Prop: ${prop}, op: ${op}, value: ${value}`);
      throw e;
    }
  };

  private calcNotExpression = (subExpression: any) => {
    const result = this.calc(subExpression);
    return !result;
  };

  private calc = (data: any[] | undefined) => {
    if (!data?.length) {
      return false;
    }

    const tempResult = [];
    for (let i = 0; i < data.length; i++) {
      const d = data[i];

      if (Array.isArray(d)) {
        tempResult.push(this.calc(d));
      } else if (this.logicalOperators.find((o) => o === d)) {
        tempResult.push(d);
      } else if (d === "not") {
        const subExpression = data[++i];
        tempResult.push(this.calcNotExpression(subExpression));
      } else {
        const prop = d;
        const op = data[++i];
        const tempValue = data[++i] as string;
        const value = trim(tempValue as string);

        tempResult.push(
          this.calcSimpleExpression(
            prop,
            op,
            tempValue === "null" ? null : tempValue === "undefined" ? undefined : value
          )
        );
      }
    }

    let result = tempResult[0] as number;
    for (let i = 1; i < tempResult.length; i++) {
      if (tempResult[i] === "and") {
        result &= tempResult[++i];
      } else if (tempResult[i] === "or") {
        result |= tempResult[++i];
      }
    }

    return !!result;
  };

  public parse(object: any) {
    //this.object = objectUtils.deepClone(object);
    this.object = object;

    const temp = groupParentheses(this.expression);
    const result = this.calc(temp);

    return result;
  }
}

const reverseFindFunction = (str: string, pos: number) => {
  let c: any;
  let begin = pos;
  while ((c = str[begin])) {
    if (c === " " || c === "(" || c === ")") {
      begin++;
      break;
    }

    begin--;
  }

  const word = str.slice(begin, pos + 1);
  if (word.indexOf("hasOwnProperty") >= 0) {
    return begin;
  }
  return -1;
};

export function groupParentheses(str: string) {
  let i = 0;
  const main = (): any => {
    const arr = [];
    let startIndex = i;
    function addWord() {
      if (i - 1 > startIndex) {
        let endIndex = i - 1;
        if (str[startIndex] === "'" && str[endIndex - 1] !== "'") {
          const newI = str.indexOf("'", i);
          if (newI >= 0) {
            endIndex = newI + 1;
            i = endIndex;
          }
        }

        let beginWordIndex = startIndex;
        if (str[startIndex - 1] === "(" && str[endIndex] === ")") {
          const beginWordPos = reverseFindFunction(str, startIndex - 2);
          if (beginWordPos >= 0) {
            beginWordIndex = beginWordPos;
            endIndex++;
          }
        }

        const word = str.slice(beginWordIndex, endIndex);
        arr.push(word);
      }
    }
    while (i < str.length) {
      switch (str[i++]) {
        case " ":
          addWord();
          startIndex = i;
          continue;
        case "(":
          arr.push(main());
          startIndex = i;
          continue;
        case ")":
          addWord();
          return arr;
      }
    }
    addWord();
    return arr;
  };
  return main();
}

const trim = (s: string) => {
  return s?.replace(/^'|'$/g, "");
};

export const evaluateODataLogicalExpression = (expression: string, object: any) => {
  // if (expression === "1 eq 1") {
  //   logger.debug("dd");
  // }
  if (expression.startsWith("not(")) {
    expression = expression.replace("not(", "not (");
  }

  // TODO: revert back `(${expression})` to expression
  return new LogicalODataExpression(`(${expression})`).parse(object);
};

export default LogicalODataExpression;
